import { gsap } from 'gsap';

interface AnimatableProperty {
    previous: number;
    current: number;
    amt: number;
}

interface RevealImageDOM {
    el: HTMLElement;
    textInner: Element | null;
    reveal: HTMLDivElement;
    revealInner: HTMLDivElement;
    revealImage: HTMLDivElement;
}

export default class reveralImage {
    DOM: RevealImageDOM;
    mousepos: { x: number; y: number };
    mousePosCache: { x: number; y: number };
    direction: { x: number; y: number };
    bounds: { el: DOMRect; reveal: DOMRect };
    animatableProperties: Record<'tx' | 'ty' | 'rotation' | 'brightness', AnimatableProperty>;
    mouseenterFn: () => void = () => {};
    mouseleaveFn: () => void = () => {};
    tl?: gsap.core.Timeline;
    requestId?: number;
    firstRAFCycle = false;

    constructor(el: HTMLElement) {
        this.DOM = {
            el,
            textInner: el.querySelector('span'),
            reveal: document.createElement('div'),
            revealInner: document.createElement('div'),
            revealImage: document.createElement('div')
        };
        this.mousepos = { x: 0, y: 0 };
        this.animatableProperties = {
            tx: { previous: 0, current: 0, amt: 0.08 },
            ty: { previous: 0, current: 0, amt: 0.08 },
            rotation: { previous: 0, current: 0, amt: 0.08 },
            brightness: { previous: 1, current: 1, amt: 0.08 }
        };

        this.mousePosCache = this.mousepos;
        this.bounds = {
            el: this.DOM.el.getBoundingClientRect(),
            reveal: this.DOM.reveal.getBoundingClientRect()
        };
        this.direction = {
            x: this.mousePosCache.x - this.mousepos.x,
            y: this.mousePosCache.y - this.mousepos.y
        };

        this.layout();
        this.initEvents();
        window.addEventListener('mousemove', ev => {
            this.mousepos = getMousePos(ev, true) as { x: number; y: number };
        });
    }
    layout() {
        // this is the element that gets its position animated (and perhaps other properties like the rotation etc..)
        this.DOM.reveal = document.createElement('div');
        this.DOM.reveal.className = 'hover-reveal';
        this.DOM.reveal.style.perspective = '1000px';
        // the next two elements could actually be only one, the image element
        // adding an extra wrapper (revealInner) around the image element with overflow hidden, gives us the possibility to scale the image inside
        this.DOM.revealInner = document.createElement('div');
        this.DOM.revealInner.className = 'hover-reveal__inner';
        this.DOM.revealImage = document.createElement('div');
        this.DOM.revealImage.className = 'hover-reveal__img';
        this.DOM.revealImage.style.backgroundImage = `url(${this.DOM.el.getAttribute('data-media')})`;

        this.DOM.revealInner.appendChild(this.DOM.revealImage);
        this.DOM.reveal.appendChild(this.DOM.revealInner);
        this.DOM.el.appendChild(this.DOM.reveal);
    }
    // calculate the position/size of both the menu item and reveal element
    calcBounds() {
        this.bounds = {
            el: this.DOM.el.getBoundingClientRect(),
            reveal: this.DOM.reveal.getBoundingClientRect()
        };
    }
    // bind some events
    initEvents() {
        this.mouseenterFn = () => {
            // show the image element
            this.showImage();
            this.firstRAFCycle = true;
            this.loopRender();
        };
        this.mouseleaveFn = () => {
            this.stopRendering();
            this.hideImage();
        };

        this.DOM.el.addEventListener('mouseenter', this.mouseenterFn);
        this.DOM.el.addEventListener('mouseleave', this.mouseleaveFn);
    }
    // show the image element
    showImage() {
        // kill any current tweens
        gsap.killTweensOf(this.DOM.revealInner);
        gsap.killTweensOf(this.DOM.revealImage);

        this.tl = gsap
            .timeline({
                onStart: () => {
                    this.DOM.reveal.style.opacity = '1';
                    gsap.set(this.DOM.el, {
                        zIndex: 99
                    });
                }
            })
            // animate the image wrap
            .to(this.DOM.revealInner, {
                ease: 'Expo.easeOut',
                startAt: { rotation: 0 },
                scale: 1,
                duration: 0.8,
                opacity: 1
            })
            .to(
                this.DOM.revealImage,
                {
                    ease: 'Expo.easeOut',
                    startAt: { filter: 'blur(4px)' },
                    duration: 1.2,
                    filter: 'blur(0px)'
                },
                0
            );
    }
    // hide the image element
    hideImage() {
        // kill any current tweens
        gsap.killTweensOf(this.DOM.revealInner);
        gsap.killTweensOf(this.DOM.revealImage);

        this.tl = gsap
            .timeline({
                onStart: () => {
                    gsap.set(this.DOM.el, { zIndex: 1 });
                },
                onComplete: () => {
                    gsap.set(this.DOM.reveal, { opacity: 0 });
                }
            })
            .to(this.DOM.revealInner, {
                ease: 'Expo.easeOut',
                duration: 0.5,
                opacity: 0
            });
    }
    // start the render loop animation (rAF)
    loopRender() {
        if (!this.requestId) this.requestId = requestAnimationFrame(() => this.render());
    }
    // stop the render loop animation (rAF)
    stopRendering() {
        if (this.requestId) {
            window.cancelAnimationFrame(this.requestId);
            this.requestId = undefined;
        }
    }
    // translate the item as the mouse moves
    render() {
        this.requestId = undefined;
        // calculate position/sizes the first time
        if (this.firstRAFCycle) {
            this.calcBounds();
        }

        // calculate the mouse distance (current vs previous cycle)
        const mouseDistanceX = clamp(Math.abs(this.mousePosCache.x - this.mousepos.x), 0, 100);
        // direction where the mouse is moving
        this.direction = { x: this.mousePosCache.x - this.mousepos.x, y: this.mousePosCache.y - this.mousepos.y };
        // updated cache values
        this.mousePosCache = { x: this.mousepos.x, y: this.mousepos.y };

        // new translation values
        // the center of the image element is positioned where the mouse is
        this.animatableProperties.tx.current = Math.abs(this.mousepos.x - this.bounds.el.left) - this.bounds.reveal.width / 2;
        this.animatableProperties.ty.current = Math.abs(this.mousepos.y - this.bounds.el.top) - this.bounds.reveal.height / 2;
        // new rotation value
        this.animatableProperties.rotation.current = this.firstRAFCycle ? 0 : map(mouseDistanceX, 0, 100, 0, this.direction.x < 0 ? 60 : -60);
        // new filter value
        this.animatableProperties.brightness.current = this.firstRAFCycle ? 1 : map(mouseDistanceX, 0, 100, 1, 2);

        // set up the interpolated values
        // for the first cycle, both the interpolated values need to be the same so there's no "lerped" animation between the previous and current state
        this.animatableProperties.tx.previous = this.firstRAFCycle
            ? this.animatableProperties.tx.current
            : lerp(this.animatableProperties.tx.previous, this.animatableProperties.tx.current, this.animatableProperties.tx.amt);
        this.animatableProperties.ty.previous = this.firstRAFCycle
            ? this.animatableProperties.ty.current
            : lerp(this.animatableProperties.ty.previous, this.animatableProperties.ty.current, this.animatableProperties.ty.amt);
        this.animatableProperties.rotation.previous = this.firstRAFCycle
            ? this.animatableProperties.rotation.current
            : lerp(this.animatableProperties.rotation.previous, this.animatableProperties.rotation.current, this.animatableProperties.rotation.amt);
        this.animatableProperties.brightness.previous = this.firstRAFCycle
            ? this.animatableProperties.brightness.current
            : lerp(
                  this.animatableProperties.brightness.previous,
                  this.animatableProperties.brightness.current,
                  this.animatableProperties.brightness.amt
              );

        // set styles
        gsap.set(this.DOM.reveal, {
            x: this.animatableProperties.tx.previous,
            y: this.animatableProperties.ty.previous,
            rotation: this.animatableProperties.rotation.previous,
            filter: `brightness(${this.animatableProperties.brightness.previous})`
        });

        // loop
        this.firstRAFCycle = false;
        this.loopRender();
    }
}
